import axios from "axios";
import { API_URL } from "../constant";

const API_BASE_URL = API_URL || "https://staging.api.topcoinuk.com/api";

// Create axios instance with auth headers
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const agentService = {
  // Get all agents with pagination and filters
  getAllagents: async (params = {}) => {
    const response = await api.get("/superadmin/agents", { params });
    return response.data;
  },

  // Get specific agent details
  getagent: async (userId) => {
    const response = await api.get(`/superadmin/agents/${userId}`);
    return response.data;
  },

  // Create new agent
  createAgent: async (agentData) => {
    const response = await api.post("/superadmin/agents", agentData);
    return response.data;
  },

  // Update agent
  updateagent: async (userId, agentData) => {
    const response = await api.put(`/superadmin/agents/${userId}`, agentData);
    return response.data;
  },

  // Delete agent
  deleteagent: async (userId) => {
    const response = await api.delete(`/superadmin/agents/${userId}`);
    return response.data;
  },

  // Update agent status
  updateagentStatus: async (userId, statusData) => {
    const response = await api.post(
      `/superadmin/agents/${userId}/status`,
      statusData,
    );
    return response.data;
  },

  // Get agent status history
  getagentStatusHistory: async (userId) => {
    const response = await api.get(
      `/superadmin/agents/${userId}/status-history`,
    );
    return response.data;
  },

  // Get agent wallet details
  getagentWallet: async (userId) => {
    const response = await api.get(`/superadmin/agents/${userId}/wallet`);
    return response.data;
  },

  // Reset agent password
  resetagentPassword: async (userId, passwordData) => {
    const response = await api.post(
      `/superadmin/agents/${userId}/reset-password`,
      passwordData,
    );
    return response.data;
  },

  getagentTransactionHistory: async (agentId, params = {}) => {
    try {
      const response = await api.get(
        `/superadmin/agents/${agentId}/transaction-history`,
        { params },
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  checkEmailAvailability: async (email) => {
    try {
      const response = await api.post(
        "/superadmin/agents/check-email-availability",
        { email },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  checkPhoneAvailability: async (phone) => {
    try {
      const response = await api.post(
        "/superadmin/agents/check-phone-availability",
        { phone },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAgentCount: async (filters = {}) => {
    try {
      const response = await api.get("/superadmin/count/agents", {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching agent count:", error);
      throw error;
    }
  },

  // Get agent referral history (for superadmin)
  getAgentReferralHistory: async (agentId = null, params = {}) => {
    try {
      let url = "/superadmin/referral-history/agent";

      // If agentId is provided, fetch specific agent's referrals
      if (agentId) {
        url = `/superadmin/referral-history/agent/${agentId}`;
      }

      const response = await api.get(url, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching agent referral history:", error);
      throw error.response?.data || error;
    }
  },

  // Add this method for regular agents viewing their own referrals
  getOwnReferralHistory: async (params = {}) => {
    try {
      const response = await api.get("/agent/referral-history", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching own referral history:", error);
      throw error.response?.data || error;
    }
  },

  // Promote agent to Senior Business Ads Agent
  promoteAgentToSenior: async (agentId, notes = "") => {
    try {
      const response = await api.post(`/superadmin/promote/agents/${agentId}`, {
        notes,
      });
      return response.data;
    } catch (error) {
      console.error("Error promoting agent:", error);
      throw error.response?.data || error;
    }
  },

  // Demote agent to Mini Business Ads Agent
  demoteAgentToMini: async (agentId, notes) => {
    try {
      const response = await api.post(`/superadmin/demote/agents/${agentId}`, {
        notes,
      });
      return response.data;
    } catch (error) {
      console.error("Error demoting agent:", error);
      throw error.response?.data || error;
    }
  },

  // Optional: Get agent role change history
  getAgentRoleHistory: async (agentId) => {
    try {
      const response = await api.get(
        `/role-history/superadmin/agents/${agentId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting role history:", error);
      throw error.response?.data || error;
    }
  },

  // Get all available admin areas with filters
  getAvailableAdminAreas: async (params = {}) => {
    try {
      const response = await api.get("/superadmin/admin-areas/available", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching admin areas:", error);
      throw error.response?.data || error;
    }
  },

  // Get counties for filtering
  getCounties: async () => {
    try {
      const response = await api.get("/superadmin/business-agents/counties");
      return response.data;
    } catch (error) {
      console.error("Error fetching counties:", error);
      throw error.response?.data || error;
    }
  },

  // Get agent's assigned admin areas
  getAgentAdminAreas: async (agentId) => {
    try {
      const response = await api.get(
        `/superadmin/business-agents/${agentId}/admin-areas`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching agent admin areas:", error);
      throw error.response?.data || error;
    }
  },

  // Assign admin areas to agent
  assignAdminAreas: async (agentId, adminAreaIds, replaceExisting = false) => {
    try {
      const response = await api.post(
        `/superadmin/business-agents/${agentId}/assign-admin-areas`,
        {
          admin_area_ids: adminAreaIds,
          replace_existing: replaceExisting,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning admin areas:", error);
      throw error.response?.data || error;
    }
  },

  // Remove admin areas from agent
  removeAdminAreas: async (agentId, adminAreaIds) => {
    try {
      const response = await api.delete(
        `/superadmin/business-agents/${agentId}/remove-admin-areas`,
        {
          data: { admin_area_ids: adminAreaIds },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error removing admin areas:", error);
      throw error.response?.data || error;
    }
  },

  // Get agents by admin area
  getAgentsByAdminArea: async (adminAreaId) => {
    try {
      const response = await api.get(
        `/superadmin/admin-areas/${adminAreaId}/agents`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching agents by admin area:", error);
      throw error.response?.data || error;
    }
  },

  // Assign focus countries to a business agent
  assignFocusCountries: async (
    agentId,
    focusCountriesData,
    replaceExisting = false,
  ) => {
    try {
      const response = await api.post(
        `/superadmin/business-agents/${agentId}/focus-countries`,
        {
          focus_countries: focusCountriesData,
          replace_existing: replaceExisting,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning focus countries:", error);
      throw error.response?.data || error;
    }
  },

  // Get focus countries for a specific business agent
  getAgentFocusCountries: async (agentId) => {
    try {
      const response = await api.get(
        `/superadmin/business-agents/${agentId}/focus-countries`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching agent focus countries:", error);
      throw error.response?.data || error;
    }
  },

  // Remove specific focus countries from a business agent
  removeFocusCountries: async (agentId, countryIds) => {
    try {
      const response = await api.delete(
        `/superadmin/business-agents/${agentId}/focus-countries`,
        {
          data: { country_ids: countryIds },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error removing focus countries:", error);
      throw error.response?.data || error;
    }
  },

  // Update focus country type or notes for a business agent
  updateFocusCountry: async (agentId, countryId, updateData) => {
    try {
      const response = await api.put(
        `/superadmin/business-agents/${agentId}/focus-countries/${countryId}`,
        updateData,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating focus country:", error);
      throw error.response?.data || error;
    }
  },

  // Get available countries for focus (excluding already assigned ones for the agent)
  getAvailableFocusCountries: async (agentId, filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.search) params.append("search", filters.search);

      const response = await api.get(
        `/superadmin/business-agents/${agentId}/available-focus-countries?${params}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching available focus countries:", error);
      throw error.response?.data || error;
    }
  },

  // Get all agents with their focus countries (Super Admin only)
  getAllAgentsWithFocusCountries: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.agent_role && filters.agent_role !== "all")
        params.append("agent_role", filters.agent_role);
      if (filters.focus_type) params.append("focus_type", filters.focus_type);
      if (filters.country_id) params.append("country_id", filters.country_id);
      if (filters.per_page) params.append("per_page", filters.per_page);

      const response = await api.get(
        `/superadmin/business-agents/focus-countries/all?${params}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching all agents with focus countries:", error);
      throw error.response?.data || error;
    }
  },

  // Get focus countries statistics (Super Admin only)
  getFocusCountriesStatistics: async () => {
    try {
      const response = await api.get(
        `/superadmin/business-agents/focus-countries/statistics`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching focus countries statistics:", error);
      throw error.response?.data || error;
    }
  },
};
